import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Formik, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useEmailValidation } from './Shared/hooks'
import {
  EmailInput,
  PWInput,
  PreNextButton,
  PostNextButton,
  PreSignButton,
  PostSignButton,
  LoginContainer,
  MainContainer,
  FormContainer,
  LogoAndHeaderContainer,
  LoginNextStep,
  FormErrorMessage,
  FormHeader,
  InputContainer,
  ForgotPWLink,
  FormLabel,
  PassEye,
  PassEyeVis,
  CommonDiv,
  PageLogo,
  ErrorMark,
  PageForm,
  SignInByOtherText
} from './styles'
import useShallowEqualSelector from 'hooks/global'
import { ApplicationState } from 'Store/intialState'

type Props = {
  email: string
  login: (
    username: string,
    password: string,
    formRef: React.MutableRefObject<null>,
    goToHauls: () => void
  ) => void
  error: string
  history: {
    push: (path: string) => void
  }
  loginSms: (
    phoneNumber: string,
    formRef: React.MutableRefObject<null>,
    goToHauls: () => void
  ) => void
  loginSmsVerify: (
    phoneNumber: string,
    smsCode: string,
    formRef: React.MutableRefObject<null>,
    goToHauls: () => void
  ) => void
  isLoggingIn: boolean
}

type FormValues = {
  username: string
  password: string
}

type FormValuesSms = {
  phoneNumber: string
  smsCode: string
}

enum SignInMethod {
  email = 'email',
  sms = 'sms'
}

const Login = (props: Props) => {
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false)
  const formRef = useRef(null)
  const [email, setEmail] = useState<string>('')
  const [next, setNext] = useState<boolean>(true)
  const [password, setPassword] = useState<string>('')
  const { emailValidator } = useEmailValidation(email)
  const [signInMethod, setSignInMethod] = useState<SignInMethod>(SignInMethod.email)
  const formRefSms = useRef(null)
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [smsCode, setSmsCode] = useState<string>('')

  const { isMobile } = useShallowEqualSelector((state: ApplicationState) => ({
    isMobile: state.ui.screenType === 'mobile'
  }))

  const login = (values: FormValues) => {
    const { username, password } = values
    props.login(username, password, formRef, goToHauls)
  }

  const loginSms = (values: FormValuesSms) => {
    const { phoneNumber } = values
    props.loginSms(phoneNumber, formRefSms, () => setNext(false))
  }

  const loginSmsVerify = (values: FormValuesSms) => {
    const { phoneNumber, smsCode } = values
    props.loginSmsVerify(phoneNumber, smsCode, formRefSms, goToHauls)
  }

  const goToHauls = () => {
    props.history.push('/app/active')
  }

  const checkLogin = () => {
    const token = localStorage.getItem('userToken')
    if (token) {
      props.history.push('/app/active')
    }
  }

  const emailInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const passwordInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const phoneNumberInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value)
  }

  const smsCodeInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSmsCode(e.target.value)
  }

  const nextStep = () => {
    setNext(!next)
  }

  const renderSignInButton = () => {
    if (
      (signInMethod === SignInMethod.email && password.length >= 6) ||
      (signInMethod === SignInMethod.sms && smsCode.length === 6)
    ) {
      return (
        <PostSignButton type="submit" disabled={props.isLoggingIn}>
          Sign In
        </PostSignButton>
      )
    }
    return <PreSignButton disabled>Sign In</PreSignButton>
  }

  const renderNextButton = () => {
    if (
      (signInMethod === SignInMethod.email && emailValidator) ||
      signInMethod === SignInMethod.sms
    ) {
      return <PostNextButton onClick={nextStep}>Next</PostNextButton>
    }
    return <PreNextButton disabled>Next</PreNextButton>
  }

  const renderEyeIconPw = () => {
    if (passwordVisible) {
      return <PassEyeVis onClick={() => setPasswordVisible(!passwordVisible)} />
    }
    return <PassEye onClick={() => setPasswordVisible(!passwordVisible)} />
  }

  useEffect(() => {
    checkLogin()
  }, []) //eslint-disable-line

  return signInMethod === SignInMethod.email ? (
    <MainContainer>
      <LoginContainer>
        <Formik
          enableReinitialize={false}
          initialValues={{
            username: '',
            password: ''
          }}
          validationSchema={LoginSchema}
          onSubmit={(values: FormValues) => {
            login(values)
          }}
          ref={formRef}
          render={({ values, handleBlur, handleChange, handleSubmit, errors, touched }) => (
            <PageForm onSubmit={handleSubmit}>
              <FormContainer>
                <CommonDiv>
                  <LogoAndHeaderContainer>
                    <div>
                      <PageLogo src="./assets/SignInLogo.png" alt="HaulR Logo" />
                    </div>
                    <div>
                      <FormHeader>Sign in</FormHeader>
                    </div>
                  </LogoAndHeaderContainer>
                  {next ? (
                    <LoginNextStep>
                      <InputContainer>
                        <FormLabel error={errors.username ? true : false}>
                          E-mail address
                          <EmailInput
                            name="username"
                            label="username"
                            className="loginInputBox"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.username}
                            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                              emailInputHandler(e)
                            }
                            fieldError={errors.username && touched.username ? true : false}
                          />
                        </FormLabel>
                        {isMobile && (
                          <SignInByOtherText onClick={() => setSignInMethod(SignInMethod.sms)}>
                            Sign in with phone number
                          </SignInByOtherText>
                        )}
                        <ErrorMessage
                          name="username"
                          render={msg => (
                            <FormErrorMessage>
                              <ErrorMark />
                              {msg}
                            </FormErrorMessage>
                          )}
                        />
                      </InputContainer>
                      {renderNextButton()}
                    </LoginNextStep>
                  ) : (
                    <LoginNextStep>
                      <InputContainer>
                        <FormLabel>
                          Password
                          <PWInput
                            label="Password"
                            name="password"
                            type={passwordVisible ? 'text' : 'password'}
                            className="loginInputBox"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                              passwordInputHandler(e)
                            }
                            fieldError={errors.password && touched.password ? true : false}
                            passwordError={props.error ? true : false}
                          />
                          {renderEyeIconPw()}
                        </FormLabel>
                        <ErrorMessage
                          name="password"
                          render={msg => (
                            <FormErrorMessage>
                              <ErrorMark />
                              {msg}
                            </FormErrorMessage>
                          )}
                        />
                        {props.error ? (
                          <FormErrorMessage>
                            <ErrorMark />
                            {props.error}
                          </FormErrorMessage>
                        ) : (
                          ''
                        )}
                        <Link to="/forgotpassword">
                          <ForgotPWLink
                            fieldError={errors.username && touched.username ? true : false}
                            passwordError={props.error ? true : false}
                          >
                            Forgot your password?
                          </ForgotPWLink>
                        </Link>
                      </InputContainer>
                      {renderSignInButton()}
                    </LoginNextStep>
                  )}
                </CommonDiv>
              </FormContainer>
            </PageForm>
          )}
        />
      </LoginContainer>
    </MainContainer>
  ) : (
    <MainContainer>
      <LoginContainer>
        <Formik
          enableReinitialize={false}
          initialValues={{
            phoneNumber: '',
            smsCode: ''
          }}
          validationSchema={LoginSmsSchema}
          onSubmit={(values: FormValuesSms) => {
            loginSmsVerify(values)
          }}
          ref={formRefSms}
          render={({ values, handleBlur, handleChange, handleSubmit, errors, touched }) => {
            return (
              <PageForm onSubmit={handleSubmit}>
                <FormContainer>
                  <CommonDiv>
                    <LogoAndHeaderContainer>
                      <div>
                        <PageLogo src="./assets/SignInLogo.png" alt="HaulR Logo" />
                      </div>
                      <div>
                        <FormHeader>Sign in</FormHeader>
                      </div>
                    </LogoAndHeaderContainer>
                    {next ? (
                      <LoginNextStep>
                        <InputContainer>
                          <FormLabel error={errors.phoneNumber ? true : false}>
                            Phone Number
                            <EmailInput
                              name="phoneNumber"
                              label="phoneNumber"
                              className="loginInputBox"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.phoneNumber}
                              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                                phoneNumberInputHandler(e)
                              }
                              fieldError={errors.phoneNumber && touched.phoneNumber ? true : false}
                              maxLength={10}
                            />
                          </FormLabel>
                          <SignInByOtherText onClick={() => setSignInMethod(SignInMethod.email)}>
                            Sign in with email
                          </SignInByOtherText>
                          <ErrorMessage
                            name="phoneNumber"
                            render={msg => (
                              <FormErrorMessage>
                                <ErrorMark />
                                {msg}
                              </FormErrorMessage>
                            )}
                          />
                        </InputContainer>
                        <PostNextButton
                          disabled={
                            phoneNumber === '' || (phoneNumber && phoneNumber.length !== 10)
                              ? true
                              : false
                          }
                          onClick={() => loginSms(values)}
                          type="button"
                        >
                          Next
                        </PostNextButton>
                      </LoginNextStep>
                    ) : (
                      <LoginNextStep>
                        <InputContainer>
                          <FormLabel>
                            SMS Code
                            <PWInput
                              label="SMS Code"
                              name="smsCode"
                              type={'text'}
                              className="loginInputBox"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.smsCode}
                              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                                smsCodeInputHandler(e)
                              }
                              fieldError={errors.smsCode && touched.smsCode ? true : false}
                              passwordError={props.error ? true : false}
                              maxLength={6}
                            />
                          </FormLabel>
                          <ErrorMessage
                            name="smsCode"
                            render={msg => (
                              <FormErrorMessage>
                                <ErrorMark />
                                {msg}
                              </FormErrorMessage>
                            )}
                          />
                          {props.error ? (
                            <FormErrorMessage>
                              <ErrorMark />
                              {props.error}
                            </FormErrorMessage>
                          ) : (
                            ''
                          )}
                          <Link to="/forgotpassword">
                            <ForgotPWLink
                              fieldError={errors.phoneNumber && touched.phoneNumber ? true : false}
                              passwordError={props.error ? true : false}
                            >
                              Didn't receive a code?
                            </ForgotPWLink>
                          </Link>
                        </InputContainer>
                        {renderSignInButton()}
                      </LoginNextStep>
                    )}
                  </CommonDiv>
                </FormContainer>
              </PageForm>
            )
          }}
        />
      </LoginContainer>
    </MainContainer>
  )
}

export default Login

const LoginSchema = Yup.object().shape({
  username: Yup.string()
    .email('Email address not found')
    .min(3, 'Too Short!')
    .required('Please enter an email address'),
  password: Yup.string().required('Please enter a password')
})

const LoginSmsSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .length(10, 'Phone number must be 10 digits')
    .matches(/^[0-9]{0,}$/, 'No spaces or special characters allowed'),
  smsCode: Yup.string()
    .length(6, 'Code must be 6 digits long')
    .matches(/^[0-9]{0,}$/, 'No spaces or special characters allowed')
})
